import { booleanAttribute, ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { TranslateDirective } from 'i18n';

/** Which honour the badge stands for — two separate distinctions, held together. */
export type FoundingBadgeKind = 'member' | 'practitioner';

/**
 * "Founding Member" badge — the permanent honour for the first 1.000 paying
 * WolfPack Health Club members.
 *
 * Renders nothing at all unless the user actually holds the status, so it can be
 * dropped next to any name without a surrounding `@if`:
 *
 *   <founding-member-badge [foundingMember]="user.founding_member"
 *                          [memberNumber]="user.founding_member_number" />
 *
 * `kind="practitioner"` renders the same badge for the "Founding Practitioner"
 * honour (the first 1.000 Practitioner Network members) — same inputs, the
 * `founding_practitioner.*` wording and its own colour. A member may hold both, so
 * the two are rendered side by side rather than one replacing the other.
 *
 * Use `compact` where space is tight (list rows, inline mentions): only the wolf
 * mark is shown, the full title stays in the tooltip.
 */
@Component({
    selector: 'founding-member-badge',
    standalone: true,
    templateUrl: './founding-member-badge.component.html',
    styleUrls: ['./founding-member-badge.component.scss'],
    imports: [TranslateDirective],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FoundingMemberBadgeComponent {
    /** Whether the user holds the status. Anything but `true` hides the badge. */
    @Input() foundingMember: boolean | null | undefined = false;

    /** The founding number (1..1000). Shown in the tooltip when present. */
    @Input() memberNumber: number | null | undefined = null;

    /** Mark only, no label — for dense lists. Usable as a bare attribute. */
    @Input({ transform: booleanAttribute }) compact = false;

    /** Which honour this badge stands for. Defaults to the Founding Member. */
    @Input() kind: FoundingBadgeKind = 'member';

    /** The `common` locale node this badge's wording comes from. */
    get i18nScope(): string {
        return this.kind === 'practitioner' ? 'founding_practitioner' : 'founding_member';
    }

    get visible(): boolean {
        // A number alone is enough: any backend that reports one means the status
        // is held, even if it predates the explicit boolean.
        return this.foundingMember === true || (this.memberNumber ?? 0) > 0;
    }

    /** "Founding Member #42" when the number is known, plain title otherwise. */
    tooltip(t: (key: string, params?: Record<string, any>) => string): string {
        const title = this.memberNumber
            ? this.tr(t, 'numbered', { number: this.memberNumber })
            : this.tr(t, 'title');
        return `${title} — ${this.tr(t, 'tooltip')}`;
    }

    /**
     * One wording of this badge's own locale node, falling back to the Founding
     * Member node when the node does not carry it. Only `badge` and `tooltip` are
     * guaranteed for every kind, and a raw key in a tooltip is worse than the
     * sibling honour's phrasing.
     */
    private tr(
        t: (key: string, params?: Record<string, any>) => string,
        field: string,
        params?: Record<string, any>
    ): string {
        const key = `${this.i18nScope}.${field}`;
        const translated = t(key, params);
        if (translated && translated !== key) return translated;
        return t(`founding_member.${field}`, params);
    }
}
